import React, { useRef, useEffect } from "react";
import useBoxes from "../hooks/useBoxes";
import useManagerHasLocation from "../hooks/useManagerHasLocation";
import DatePicker from "react-datepicker";
import Popup from "reactjs-popup";
import _ from "underscore";
import { excludeDates } from "../utility/momentPoly";
import de from "date-fns/locale/de";
import { Weekday, DailyComponent, CheckBoxes } from "../ui_components/uiExporter";
import SaveNewPeriod from "../components/saveNewPeriod";
import { displayOptions, displayOptionsI18n, holidays } from "../utility/constants";
import moment from "moment";
import t from "../../../helpers/t";
// import "./boxes.scss"

const uuid = _.uniqueId;

/**
 * As requested in #21493, a user of role manager-location who is not owner
 * of the active location may see the location but not edit it.
 * For now, this is done by adding some custom css and hiding the according save buttons
 */
const Boxes = ({ data, setCurrentSelected, active, dropdown, setDropdown }) => {
  const {
    date,
    handleCopy,
    handleChange,
    setChecked,
    checked,
    handleDelete,
    sortdays,
    daily,
    handlePeriodType,
    store,
    handleSelected,
    noClick,
    anytime,
    checkTrigger,
    setCheckTrigger,
  } = useBoxes({ data, setDropdown, dropdown });

  const { managerHasLocation } = useManagerHasLocation();

  const isPast = moment(new Date(date)).isBefore(moment()) && !active;
  const isHoliday =
    data.type === "holiday" ||
    holidays
      .map((x) => Object.values(x)[0])
      .indexOf(moment(new Date(date)).format("YYYY-MM-DD")) !== -1;

  const boxRef = useRef(null);

  // Scroll to the box when it is clicked via CSS Scroll Snap
  const scrollToBox = (e) => {
    const focused = e.currentTarget.closest(".focused");
    if (focused) {
      focused.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });

      window.scrollTo({
        top: window.scrollY,
      });
    }
  };

  // Center box on first load
  useEffect(() => {
    if (active && boxRef.current) {
      scrollToBox({ currentTarget: boxRef.current });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <section
      style={{
        flex: 1,
        display: "flex",
        ...(managerHasLocation ? null : { cursor: "no-drop" }),
      }}
    >
      <div
        key={uuid()}
        ref={boxRef}
        id={data.id}
        className={`box child ${noClick ? "box--no-click" : ""}`}
        style={!managerHasLocation ? { pointerEvents: "none" } : null}
        onClick={(e) => {
          setCurrentSelected(data.id);
          scrollToBox(e);
        }}
      >
        {managerHasLocation && (
          <Popup
            key={uuid()}
            style={{ padding: 0 }}
            trigger={
              <button className="box-menu-btn" type="button">
                ...
              </button>
            }
            position="bottom left"
          >
            {() => (
              <div className="box-popup-parent" key={uuid()}>
                <div className="box-copy-delete" onClick={(e) => handleCopy(e)}>
                  {t("copy_to_new_period")}
                </div>
                <div className="box-divider" />
                <div
                  className="box-copy-delete"
                  onClick={(e) => {
                    if (window.confirm(t("really_remove"))) {
                      handleDelete(e);
                    }
                  }}
                >
                  {t("delete_${a}", { a: t("period") })}
                </div>
              </div>
            )}
          </Popup>
        )}
        <div
          className={`checkin-item ${isPast ? "checkin-item--past" : ""} ${isHoliday ? "checkin-item--holiday" : ""}`}
          key={uuid()}
        >
          <h2 className="checkin-heading" key={uuid()}>
            {t("checkin_hours_valid_from")}
          </h2>
          <div className="period-parent" key={uuid()}></div>
          <div className="date-div">
            <DatePicker
              strictParsing
              dateFormat="dd.MM.y"
              selected={date && new Date(date).getFullYear() <= 2050 ? new Date(date) : null}
              onChange={(date) => handleChange(date)}
              locale={de}
              excludeDates={[
                ...store.filterPeriodsReducer.map((period) => excludeDates(period.valid_from)),
              ]}
              minDate={typeof data.id === "string" ? new Date() : null}
              placeholderText={
                moment(date).format("YYYY") > 2050 ? t("please_pick_valid_date") : ""
              }
            />
          </div>
          <select
            selected={dropdown[data.id]}
            onChange={(sel) => handleSelected(sel.target.value)}
            defaultValue={dropdown[data.id]}
            key={uuid()}
          >
            {displayOptions.map((option) => (
              <option key={option + uuid()} value={option}>
                {t(displayOptionsI18n[option])}
              </option>
            ))}
          </select>

          {dropdown[data.id] === "Weekdays" && (
            <Weekday
              key={uuid()}
              sortdays={sortdays}
              toDisable={true}
              periodId={data}
              checked={checked}
              anytime={anytime}
              check={checkTrigger}
              setCheck={setCheckTrigger}
            />
          )}
          {dropdown[data.id] === "Daily" && (
            <DailyComponent
              key={uuid()}
              daily={daily}
              toDisable={false}
              parentID={data.id}
              weekday={new Date(date).getDay()}
              checked={checked}
              anytime={anytime}
            />
          )}
          <div className="period-type" key={uuid()}>
            <div className="period-type__child">
              <input
                className="period-type__input"
                type="radio"
                value="period"
                checked={data.type === "period"}
                onChange={(e) => handlePeriodType(e.target.value)}
              />
              <p>{t("regular_period")}</p>
            </div>
            <div className="period-type__child">
              <input
                className="period-type__input"
                type="radio"
                value="holiday"
                checked={data.type === "holiday"}
                onChange={(e) => handlePeriodType(e.target.value)}
              />
              <p>{t("holiday")}</p>
            </div>
          </div>
          {data &&
            data.id &&
            typeof dropdown[data.id] !== "undefined" &&
            dropdown[data.id] !== "Check-In at anytime" && (
              <CheckBoxes
                key={uuid()}
                date={date}
                checked={checked}
                setChecked={setChecked}
                parentID={data.id}
              />
            )}
          {managerHasLocation && (
            <>
              {store.changes.newPeriods.length > 0 &&
                store.changes.newPeriods[0].id === data.id && <SaveNewPeriod />}
              {store.changes.patchPeriods.length > 0 &&
                store.changes.patchPeriods[0].id === data.id && <SaveNewPeriod />}
            </>
          )}
        </div>
      </div>
    </section>
  );
};
export default Boxes;

// eslint-disable-next-line no-unused-vars
const textStyle = {
  fontSize: "15px",
  margin: "5px 0 10px",
  color: "#333",
  fontWeight: 700,
};
